import {
  Client, BrowserZmlClient
} from "@zappar/zw-api-client";
import { MediaType, ExternalAssetSource } from "../../types";
import { BaseProvider } from "../base/BaseProvider";
import { IAsset, SearchParams } from "../base/types";
import { Model3DAsset } from "./assets";
import { TheBaseMeshAsset } from "./types";


/**
 * The Base Mesh Provider
 *
 * Provides read-only access to The Base Mesh's library of free CC0 3D models.
 * https://www.thebasemesh.com/
 *
 * All assets are fetched from a single manifest.json endpoint and filtered
 * client-side, as the API does not support server-side search or pagination.
 */
export class TheBaseMeshProvider extends BaseProvider {
  private client: BrowserZmlClient;
  private cachedAssets: IAsset[] | null = null;

  constructor(apiClient: Client) {
    super({
      id: ExternalAssetSource.Thebasemesh,
      name: 'thebasemesh',
      displayName: 'The Base Mesh',
      description: 'Free CC0 3D base meshes',
      baseUrl: 'https://cdn-assetimporter.zap.works/manifest.json',
      capabilities: {
        read: true,
        write: false,
        delete: false,
        supportedTypes: [MediaType.Model3D],
      },
    });

    this.client = apiClient.zml;
  }

  /**
   * Fetch and cache all assets from the manifest
   */
  private async fetchAllAssets(): Promise<IAsset[]> {
    if (this.cachedAssets) {
      return this.cachedAssets;
    }
    let response: any;
    try {
      response = await this.client.fetchViaExternalProxy(this.baseUrl, 'basemesh');
    } catch (error) {
      console.error('Failed to fetch The Base Mesh manifest:', error);
      return [];
    }

    if (!response) return [];
    this.cachedAssets = response.map((item: TheBaseMeshAsset) => new Model3DAsset(item));
    return this.cachedAssets || [];
  }

  /**
   * Search for assets in The Base Mesh.
   * All filtering is done client-side from the manifest.
   */
  async search(params: SearchParams): Promise<IAsset[]> {
    const allAssets = await this.fetchAllAssets();

    let filtered = allAssets;

    if (params.query) {
      const query = params.query.toLowerCase();
      filtered = allAssets.filter(asset =>
        asset.getName().toLowerCase().includes(query)
      );
    }

    const page = params.page || 1;
    const limit = params.limit;
    if (limit) {
      const startIndex = (page - 1) * limit;
      return filtered.slice(startIndex, startIndex + limit);
    }

    return filtered;
  }

  /**
   * Get a specific asset by ID
   */
  async getAssetById(id: string): Promise<IAsset> {
    const allAssets = await this.fetchAllAssets();
    const asset = allAssets.find(a => a.getId() === id);

    if (!asset) {
      throw new Error(`Asset with ID ${id} not found in The Base Mesh`);
    }

    return asset;
  }
}
